import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLocation } from 'react-router';

import { axiosApi } from '@/api/axios';
import { appRoutes } from '@/routes/app-routes.ts';
import { useAuthStore, useModalStore } from '@/store';
import { getLocalDate, getRemainingSeconds } from '@/utils';

import {
    CancelReservationResponse,
    GetFriendsNumbersResponse,
    GetMainNumbersParams,
    GetMyNumbersDataResponse,
    GetMyNumbersResponse,
    GetNumberPurchaseDataResponse,
    GetPrettyNumbersDataRes,
    GetPrettyNumbersParams,
    GetReservationsResponse,
    GoodTariffsDataResponse,
    GoodTariffsNumbersResponse,
    MainNumbersDataResponse,
    NumberPurchaseResponse,
    NumbersResponse,
    ReservationBody,
    ReservationResponse,
} from './types';

export const useGetMainNumbers = (
    page: GetMainNumbersParams['page'] = 0,
    sort: GetMainNumbersParams['sort'] = 'popular',
    enabled: boolean
) => {
    const location = useLocation();

    return useQuery({
        enabled: location.pathname === appRoutes.mainPage && enabled,
        queryKey: ['main-numbers', page, sort],
        queryFn: async () => {
            const { data } = await axiosApi.get<NumbersResponse>(
                `numbers/main?page=${page}&sort=${sort}`
            );

            return data.data;
        },
    });
};

export const useGetMainNumbersData = () => {
    const location = useLocation();

    return useQuery({
        enabled:
            location.pathname === appRoutes.mainPage ||
            location.pathname.includes(appRoutes.myReservations),
        queryKey: ['main-numbers-data'],
        queryFn: async () => {
            const { data } = await axiosApi.get<MainNumbersDataResponse>('/numbers/main/data');
            return data.data;
        },
    });
};

export const useGetPrettyNumbers = (
    page: GetPrettyNumbersParams['page'] = 0,
    sort: GetPrettyNumbersParams['sort'] = 'popular',
    search: string = ''
) => {
    const location = useLocation();

    return useQuery({
        enabled: location.pathname.includes(appRoutes.prettyNums),
        queryKey: ['pretty-numbers', page, sort, search],
        queryFn: async () => {
            const { data } = await axiosApi.get<NumbersResponse>(
                `numbers/all?page=${page}&sort=${sort}&${search}`
            );
            return data.data;
        },
    });
};

export const useGetPrettyNumbersData = () => {
    const location = useLocation();

    return useQuery({
        enabled: location.pathname.includes(appRoutes.prettyNums),
        queryKey: ['pretty-numbers-data'],
        queryFn: async () => {
            const { data } = await axiosApi.get<GetPrettyNumbersDataRes>('/numbers/all/data');

            return data.data;
        },
    });
};

export const useGetGoodTariffsData = (search: string = '') => {
    const { pathname } = useLocation();
    return useQuery({
        enabled: pathname.includes(appRoutes.goodTariffs),
        queryKey: ['good-tariffs-data', search],
        queryFn: async () => {
            const { data } = await axiosApi.get<GoodTariffsDataResponse>(
                `numbers/private/data?${search}`
            );

            return data.data;
        },
    });
};

export const useGetGoodTariffsNums = <T extends string>(
    page: number,
    tariffId: T,
    search: string
) => {
    const { pathname } = useLocation();
    return useQuery({
        enabled: pathname.includes(appRoutes.goodTariffs) && page > 0,
        queryKey: ['good-tariffs-nums', page, tariffId, search],
        queryFn: async () => {
            const { data } = await axiosApi.get<GoodTariffsNumbersResponse<T>>(
                `numbers/private?page=${page}&search[tariffs][]=${tariffId}&${search}`
            );

            return data.list[tariffId];
        },
    });
};

export const useGetReservations = () => {
    const { token } = useAuthStore();
    const { pathname } = useLocation();

    return useQuery({
        enabled: pathname.includes(appRoutes.myReservations),
        queryKey: ['reservations', token],
        queryFn: async () => {
            const { data } = await axiosApi.get<GetReservationsResponse>('numbers/reservations', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return data.data.filter(item => getRemainingSeconds(item.number.expiry + '000') > 0);
        },
    });
};

export const useReserveNumber = (msisdn: number) => {
    const { token } = useAuthStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['reserve-number'],
        mutationFn: async (body: ReservationBody) => {
            const { data } = await axiosApi.post<ReservationResponse>(
                `/numbers/${msisdn}/reservation`,
                body,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: status => status === 200 || status === 400,
                }
            );

            if (data.success) {
                queryClient.invalidateQueries({ queryKey: ['main-numbers'], exact: false });
                queryClient.invalidateQueries({ queryKey: ['pretty-numbers'], exact: false });
                queryClient.invalidateQueries({ queryKey: ['good-tariffs-data'], exact: false });
                queryClient.invalidateQueries({ queryKey: ['good-tariffs-nums'], exact: false });
                queryClient.invalidateQueries({ queryKey: ['reservations'], exact: false });
                queryClient.invalidateQueries({ queryKey: ['user'], exact: false });

                return data;
            }

            const numberAlreadyReservedErr = data.errors?.find(
                err => err.type === 'number_already_reserved'
            );

            if (numberAlreadyReservedErr) {
                return data;
            } else {
                throw new AxiosError('Number already reserved');
            }
        },
    });
};

export const useCancelNumberReservation = () => {
    const { token } = useAuthStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['cancel-number-reservation'],
        mutationFn: async (msisdn: number) => {
            const { data } = await axiosApi.delete<CancelReservationResponse>(
                `/numbers/${msisdn}/reservation`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            queryClient.invalidateQueries({ queryKey: ['main-numbers'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['pretty-numbers'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['good-tariffs-data'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['good-tariffs-nums'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['reservations'], exact: false });
            queryClient.invalidateQueries({ queryKey: ['user'], exact: false });

            return data;
        },
    });
};

export const useGetNumberPurchaseData = (
    msisdn: number,
    selectedTariff?: string,
    refetchInterval?: number
) => {
    const { token } = useAuthStore();
    const { setIsYooMoneyError, setIsPurchaseErrorOpen } = useModalStore();

    return useQuery({
        staleTime: 0,
        enabled: !!token,
        refetchInterval,
        queryKey: ['purchase-data', msisdn],
        queryFn: async () => {
            const { data, status: resStatus } = await axiosApi.get<GetNumberPurchaseDataResponse>(
                `numbers/${msisdn}/purchase`,
                {
                    params: {
                        selectedTariff,
                    },
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: status =>
                        (status >= 200 && status < 300) ||
                        status === 406 ||
                        status === 408 ||
                        status === 503,
                }
            );

            if (resStatus === 406 || resStatus === 408 || resStatus === 503) {
                setIsYooMoneyError(false);
                setIsPurchaseErrorOpen(true);

                return;
            }

            const {
                data: {
                    deliveryDateRanges: { dates, times } = {},
                    status,
                    paymentConfirmationToken,
                    cost,
                },
            } = data;

            const convertedData = {
                dates: [
                    {
                        label: 'Дата доставки',
                        options: dates?.map(date => ({
                            value: date,
                            label: getLocalDate(date),
                        })),
                    },
                ],
                times: [
                    {
                        label: 'Время доставки',
                        options: times?.map(time => ({
                            value: time.id,
                            label: `${time.from} - ${time.to}`,
                        })),
                    },
                ],
            };

            return { convertedData, status, paymentConfirmationToken, cost };
        },
    });
};

export const usePurchaseNumber = (msisdn: number) => {
    const { token } = useAuthStore();

    return useMutation({
        mutationKey: ['purchase-number', msisdn],
        mutationFn: async (data: FormData) => {
            const { data: resData } = await axiosApi.post<NumberPurchaseResponse>(
                `numbers/${msisdn}/purchase`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    validateStatus: status => status < 500 && status !== 401,
                }
            );

            return resData;
        },
    });
};

export const useGetMyNumbers = (period?: string) => {
    const { token } = useAuthStore();

    return useQuery({
        queryKey: ['my-numbers', token, period],
        queryFn: async () => {
            const { data } = await axiosApi.get<GetMyNumbersResponse>('numbers/my/own', {
                params: { period },
                headers: { Authorization: `Bearer ${token}` },
            });

            return data;
        },
    });
};

export const useGetMyNumbersData = () => {
    const { token } = useAuthStore();

    return useQuery({
        queryKey: ['my-numbers-data', token],
        queryFn: async () => {
            const { data } = await axiosApi.get<GetMyNumbersDataResponse>('numbers/my/data', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return data;
        },
    });
};

export const useGetFriendsNumbers = (period?: string) => {
    const { token } = useAuthStore();

    return useQuery({
        queryKey: ['friends', token, period],
        queryFn: async () => {
            const {
                data: { data },
            } = await axiosApi.get<GetFriendsNumbersResponse>('numbers/my/friends', {
                params: { period },
                headers: { Authorization: `Bearer ${token}` },
            });

            if (data) {
                return Object.entries(data).map(([key, value]) => ({
                    id: key,
                    ...value,
                }));
            }
        },
    });
};
